import { faLink } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

export interface ShareDropdownProps {
	shareDropdownRef: React.RefObject<HTMLUListElement>;
	isCopied: boolean;
	setIsCopied: (isCopied: boolean) => void;
	articleURL: string;
	title: string;
}

const ShareDropdown = ({ shareDropdownRef, isCopied, setIsCopied, articleURL }: ShareDropdownProps) => {
	const copyTextClasses = clsx('text-sm font-normal not-italic text-gray-900 hover:underline sm:text-base sm:leading-[20px]', {
		'text-success-800': isCopied
	});

	const statusRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isCopied && statusRef.current) {
			statusRef.current.textContent = 'Link copied to clipboard';
		} else if (statusRef.current) {
			statusRef.current.textContent = '';
		}
	}, [isCopied]);

	return (
		<ul
			ref={shareDropdownRef}
			className="absolute left-0 top-6 z-50 flex w-36 flex-col items-start justify-center divide-y rounded-lg bg-white shadow-[rgba(0,0,0,0.35)_0px_5px_15px] sm:top-8 sm:w-36"
			onClick={(e) => e.stopPropagation()}
		>
			<li
				onClick={async () => {
					try {
						await navigator.clipboard.writeText(articleURL);
						setIsCopied(true);
					} catch (e) {
						/* No need to do anything */
					}
				}}
				className="w-full"
			>
				<button
					className="flex w-full items-center gap-2 p-3 hover:cursor-pointer"
					data-behavior="copy_link"
					data-social-tool="copy"
					data-social-type="content"
					aria-label={isCopied ? 'Link is copied' : 'Copy Link'}
				>
					<div className="flex w-6 items-center justify-center">
						<FontAwesomeIcon icon={faLink} />
					</div>
					<span className={copyTextClasses}>{isCopied ? 'Copied!' : 'Copy Link'}</span>
					{isCopied && (
						<div className="flex w-6 items-center justify-center text-success-800">
							<FontAwesomeIcon icon={faCircleCheck} />
						</div>
					)}
				</button>

				<div ref={statusRef} className="sr-only" aria-live="polite" role="status"></div>
			</li>
		</ul>
	);
};

export default ShareDropdown;
